import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Grid,
    Alert,
    CircularProgress,
    AlertTitle,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    InputAdornment,
    IconButton,
    FormControlLabel,
    Switch
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import { companyService } from '../../services/companyService';
import { GooglePlacesAutocompleteProxy } from '../common/GooglePlacesAutocompleteProxy';
import { getTaxCodes, getTaxCodeCategories } from '../../services/taxService';
import PageHeader from '../PageHeader';
import { toast } from 'react-toastify';
import ProductTaxCodeDialog from '../tax/ProductTaxCodeDialog';
import { filterTaxIdWarning } from '../../utils/taxAccountWarningUtils';

import { isValidEin, EIN_ERROR, generateEin as generateEIN, isUSCountry } from '../../utils/ein';
// Validation schema
const validationSchema = Yup.object({
    name: Yup.string().required('Company name is required'),
    address_line1: Yup.string().required('Address line 1 is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zip_code: Yup.string().required('Postal code is required'),
    country: Yup.string().required('Country is required'),
    tax_code: Yup.string(),
    tax_id: Yup.string().required('Tax ID is required').test('ein', EIN_ERROR, function (v) { return isValidEin(v, (this.parent as any)?.country); }),
    phone: Yup.string(),
    email: Yup.string().email('Invalid email address'),
    website: Yup.string()
});

const TAX_ACCOUNT_WARNING_DEFAULT =
    'No connected account configured for accurate tax calculation. Save the company again to retry the account connection.';

// Tax code selector modal component
const TaxCodeSelector = ({ open, onClose, onSelect, selectedTaxCode }: {
    open: boolean;
    onClose: () => void;
    onSelect: (taxCode: any) => void;
    selectedTaxCode: string;
}) => {
    const [taxCodes, setTaxCodes] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');

    // Load tax codes and categories
    useEffect(() => {
        if (open) {
            loadTaxCodes();
            loadCategories();
        }
    }, [open, selectedCategory]);

    const loadTaxCodes = async () => {
        setLoading(true);
        try {
            const data = await getTaxCodes({
                category_type: selectedCategory || undefined,
                search: search || undefined,
                limit: 1000
            });
            setTaxCodes(data);
        } catch (error) {
            console.error('Failed to load tax codes:', error);
            toast.error('Failed to load tax codes');
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const data = await getTaxCodeCategories();
            setCategories(data);
        } catch (error) {
            console.error('Failed to load tax code categories:', error);
        }
    };

    const handleSearch = () => {
        loadTaxCodes();
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleCategoryChange = (e: any) => {
        setSelectedCategory(e.target.value);
    };

    const handleSelectTaxCode = (taxCode: any) => {
        onSelect(taxCode);
        onClose();
    };

    const handleDownloadCSV = () => {
        // Create CSV content from tax codes
        const headers = ["ID", "Name", "Description", "Category"];
        const csvRows = [headers];
        
        taxCodes.forEach(code => {
            csvRows.push([
                code.id,
                code.name,
                code.description || '',
                code.category_type || ''
            ]);
        });
        
        // Convert to CSV format
        const csvContent = csvRows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
        
        // Create and download the file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'tax_codes.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                        <Typography variant="h6">Select Product Tax Code</Typography>
                    </Grid>
                    <Grid item>
                        <Button 
                            startIcon={<DownloadIcon />} 
                            onClick={handleDownloadCSV}
                            variant="outlined"
                            size="small"
                        >
                            Download all PTCs
                        </Button>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Search Tax Codes"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleSearch}>
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="category-filter-label">
                                    <FilterListIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Filter by Category
                                </InputLabel>
                                <Select
                                    labelId="category-filter-label"
                                    value={selectedCategory}
                                    onChange={handleCategoryChange}
                                    label="Filter by Category"
                                >
                                    <MenuItem value="">All Categories</MenuItem>
                                    {categories.map((category, index) => (
                                        <MenuItem key={index} value={category.name}>
                                            {category.name} ({category.count})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width="15%">ID</TableCell>
                                    <TableCell width="25%">Name</TableCell>
                                    <TableCell width="45%">Description</TableCell>
                                    <TableCell width="15%">Category</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {taxCodes.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            No tax codes found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    taxCodes.map((taxCode) => (
                                        <TableRow 
                                            key={taxCode.id}
                                            hover
                                            onClick={() => handleSelectTaxCode(taxCode)}
                                            selected={selectedTaxCode === taxCode.id}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>{taxCode.id}</TableCell>
                                            <TableCell>{taxCode.name}</TableCell>
                                            <TableCell>{taxCode.description}</TableCell>
                                            <TableCell>{taxCode.category_type}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface CompanyFormProps {
    mode: 'create' | 'edit';
}

export const CompanyForm: React.FC<CompanyFormProps> = ({ mode }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: routeCompanyId } = useParams<{ id: string }>();

    const [formMode, setFormMode] = useState<'create' | 'edit'>(mode);
    const [activeCompanyId, setActiveCompanyId] = useState<number | null>(
        routeCompanyId ? parseInt(routeCompanyId, 10) : null
    );
    const [loading, setLoading] = useState(formMode === 'edit');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [taxDialogOpen, setTaxDialogOpen] = useState(false);
    const [selectedTaxCodeDetails, setSelectedTaxCodeDetails] = useState<any>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [taxAccountWarning, setTaxAccountWarning] = useState<string | null>(
        (location.state as any)?.taxAccountWarning || null
    );
    const [taxCodeOptions, setTaxCodeOptions] = useState<any[]>([]);
    const [taxCodeLoading, setTaxCodeLoading] = useState<boolean>(false);

    const initialValues = {
        name: '',
        tax_id: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'US',
        phone: '',
        email: '',
        website: '',
        tax_code: '',
        is_head_office: true
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: () => {
            /* Submission handled via custom handler */
        },
    });

    useEffect(() => {
        if (formMode === 'edit' && activeCompanyId) {
            loadCompany(activeCompanyId);
        }
    }, [formMode, activeCompanyId]);

    useEffect(() => {
        const fetchTaxCodes = async () => {
            try {
                setTaxCodeLoading(true);
                const codes = await getTaxCodes({ limit: 1000 });
                setTaxCodeOptions(codes);
            } catch (loadError) {
                console.error('Failed to load tax codes for autocomplete:', loadError);
            } finally {
                setTaxCodeLoading(false);
            }
        };

        fetchTaxCodes();
    }, []);

    useEffect(() => {
        if (!formik.values.tax_code) {
            setSelectedTaxCodeDetails(null);
            return;
        }

        const match = taxCodeOptions.find((code) => code.id === formik.values.tax_code);
        if (match) {
            setSelectedTaxCodeDetails({
                name: match.name,
                description: match.description,
            });
        }
    }, [formik.values.tax_code, taxCodeOptions]);

    useEffect(() => {
        if ((location.state as any)?.taxAccountWarning) {
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);

    const loadCompany = async (companyId: number) => {
        try {
            setLoading(true);
            const company = await companyService.getCompany(companyId);
            setActiveCompanyId(company.id);
            formik.setValues({
                name: company.name,
                tax_id: company.tax_id,
                address_line1: company.address_line1,
                address_line2: company.address_line2 || '',
                city: company.city,
                state: company.state,
                zip_code: company.zip_code,
                country: company.country,
                phone: company.phone || '',
                email: company.email || '',
                website: company.website || '',
                tax_code: company.tax_code || '',
                is_head_office: company.is_head_office || false
            });
            const resolvedWarning =
                company.tax_setup_warning ||
                (!company.tax_account_id ? TAX_ACCOUNT_WARNING_DEFAULT : null);
            setTaxAccountWarning(filterTaxIdWarning(resolvedWarning));
        } catch (err: any) {
            console.error('Error loading company:', err);
            setError(err?.response?.data?.detail || 'Failed to load company');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};
        
        // Required fields
        if (!formik.values.name) errors.name = 'Company name is required';
        if (!formik.values.tax_id) errors.tax_id = 'Tax ID is required';
        else if (!isValidEin(formik.values.tax_id, (formik.values as any).country)) errors.tax_id = EIN_ERROR;
        if (!formik.values.address_line1) errors.address_line1 = 'Address is required';
        if (!formik.values.city) errors.city = 'City is required';
        if (!formik.values.state) errors.state = 'State is required';
        if (!formik.values.zip_code) errors.zip_code = 'ZIP code is required';
        
        // Email validation
        if (formik.values.email && !/\S+@\S+\.\S+/.test(formik.values.email)) {
            errors.email = 'Please enter a valid email address';
        }
        
        // Website validation - Tax registrations require properly formatted URLs with protocol
        if (formik.values.website) {
            // Website format validation per tax registration requirements
            if (!formik.values.website.startsWith('http://') && !formik.values.website.startsWith('https://')) {
                errors.website = 'Website must start with http:// or https://';
            } else if (!/^https?:\/\/[a-zA-Z0-9][-a-zA-Z0-9.]*\.[a-zA-Z]{2,}(\/.*)?$/.test(formik.values.website)) {
                errors.website = 'Please enter a valid website URL format';
            }
        } else {
            // If website is empty, set a default value
            formik.setFieldValue('website', 'https://www.example.com');
        }
        
        // Phone validation - registrations require E.164 format
        if (formik.values.phone) {
            // Remove all non-digit characters for checking
            const digitsOnly = formik.values.phone.replace(/\D/g, '');
            
            if (digitsOnly.length < 10) {
                errors.phone = 'Phone number must have at least 10 digits';
            } else if (!/^\+?[1-9]\d{1,14}$/.test(formik.values.phone.startsWith('+') ? formik.values.phone : `+${digitsOnly}`)) {
                errors.phone = 'Phone number should be in international format (e.g., +1XXXXXXXXXX)';
            }
        } else {
            // If phone is empty, set a default value
            formik.setFieldValue('phone', '+14155550199');
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const checkForDuplicateTaxId = async (): Promise<boolean> => {
        if (!formik.values.tax_id) return false;
        
        try {
            // Get all companies to check for duplicate tax_id
            const companies = await companyService.listCompanies();
            const duplicateTaxId = companies.some(company =>
                company.tax_id === formik.values.tax_id &&
                (formMode !== 'edit' || (formMode === 'edit' && activeCompanyId !== company.id))
            );
            
            if (duplicateTaxId) {
                setValidationErrors(prev => ({
                    ...prev,
                    tax_id: 'This Tax ID is already in use by another company'
                }));
                return true;
            }
        } catch (error) {
            console.error('Error checking for duplicate tax ID:', error);
        }
        
        return false;
    };

    const generateUniqueTaxId = () => {
        // US companies validate tax ids as XX-XXXXXXX, so generate a valid EIN.
        // Non-US companies keep the free-form identifier.
        const uniqueTaxId = isUSCountry(formik.values.country)
            ? generateEIN()
            : `TAX${Date.now()}`;
        
        // Update the form data with the new Tax ID
        formik.setFieldValue('tax_id', uniqueTaxId);
        
        // Clear any validation errors for tax_id
        if (validationErrors.tax_id) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.tax_id;
                return newErrors;
            });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        formik.setFieldValue(name, value);
        
        // Clear validation error when field is changed
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleAddressChange = (address: string, addressData: any) => {
        console.log('Address selected:', address, addressData);
        formik.setFieldValue('address_line1', addressData.address_line1 || formik.values.address_line1);
        formik.setFieldValue('address_line2', addressData.address_line2 || formik.values.address_line2);
        formik.setFieldValue('city', addressData.city || formik.values.city);
        formik.setFieldValue('state', addressData.state || formik.values.state);
        formik.setFieldValue('zip_code', addressData.zip_code || formik.values.zip_code);
        formik.setFieldValue('country', addressData.country || formik.values.country);
        
        // Clear validation errors for address fields
        const addressFields = ['address_line1', 'city', 'state', 'zip_code', 'country'];
        const newErrors = { ...validationErrors };
        addressFields.forEach(field => {
            if (newErrors[field]) {
                delete newErrors[field];
            }
        });
        setValidationErrors(newErrors);
    };

    const clearAddressFields = () => {
        formik.setFieldValue('address_line1', '');
        formik.setFieldValue('city', '');
        formik.setFieldValue('state', '');
        formik.setFieldValue('zip_code', '');
        formik.setFieldValue('country', 'US');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (formMode === 'create') {
            const hasDuplicate = await checkForDuplicateTaxId();
            if (hasDuplicate) {
                return;
            }
        }

        setSubmitting(true);
        setError(null);

        try {
            if (formMode === 'create') {
                const createdCompany = await companyService.createCompany(formik.values);

                if (!createdCompany) {
                    throw new Error('Failed to create company');
                }

                if (createdCompany.tax_setup_warning || !createdCompany.tax_account_id) {
                    const warningMessage =
                        createdCompany.tax_setup_warning || TAX_ACCOUNT_WARNING_DEFAULT;
                    const normalizedWarning = filterTaxIdWarning(warningMessage);
                    setTaxAccountWarning(normalizedWarning);
                    if (normalizedWarning) {
                        toast.warning(normalizedWarning);
                    }
                    setFormMode('edit');
                    setActiveCompanyId(createdCompany.id);
                    await loadCompany(createdCompany.id);
                    return;
                }

                toast.success('Company created successfully');
                navigate('/companies');
            } else if (formMode === 'edit' && activeCompanyId) {
                const updatedCompany = await companyService.updateCompany(activeCompanyId, formik.values);

                if (!updatedCompany) {
                    throw new Error('Failed to update company');
                }

                if (updatedCompany.tax_setup_warning || !updatedCompany.tax_account_id) {
                    const warningMessage =
                        updatedCompany.tax_setup_warning || TAX_ACCOUNT_WARNING_DEFAULT;
                    const normalizedWarning = filterTaxIdWarning(warningMessage);
                    setTaxAccountWarning(normalizedWarning);
                    if (normalizedWarning) {
                        toast.warning(normalizedWarning);
                    }
                    await loadCompany(activeCompanyId);
                    return;
                }

                toast.success('Company updated successfully');
                navigate('/companies');
            }
        } catch (err: any) {
            console.error('Error saving company:', err);
            const apiDetail = err?.response?.data?.detail;
            setError(apiDetail || err.message || 'Failed to save company');

            if (err?.response?.data?.validation_errors) {
                setValidationErrors(err.response.data.validation_errors);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" mb={2}>
                {formMode === 'create' ? 'Create Company' : 'Edit Company'}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {taxAccountWarning && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {taxAccountWarning}
                </Alert>
            )}

            {/* Information about sales tax registration requirements */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>Sales Tax Registration Information</AlertTitle>
                <Typography variant="body2">
                    For sales tax registration and compliance, please provide:
                </Typography>
                <ul>
                    <li>
                        <strong>Website:</strong> A valid URL starting with http:// or https:// (e.g., https://www.example.com)
                    </li>
                    <li>
                        <strong>Phone:</strong> A phone number in international format (e.g., +14155550199)
                    </li>
                </ul>
                <Typography variant="body2">
                    These fields are required for sales tax registration and compliance purposes.
                </Typography>
            </Alert>

            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Company Name"
                                name="name"
                                value={formik.values.name}
                                onChange={handleChange}
                                required
                                error={!!validationErrors.name}
                                helperText={validationErrors.name}
                                disabled={submitting}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                                <TextField
                                    fullWidth
                                    label="Tax ID"
                                    name="tax_id"
                                    value={formik.values.tax_id}
                                    onChange={handleChange}
                                    required
                                    error={!!validationErrors.tax_id}
                                    helperText={validationErrors.tax_id}
                                    disabled={submitting}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={generateUniqueTaxId}
                                    disabled={submitting}
                                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                                >
                                    Generate Unique
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                                <Box flexGrow={1}>
                                    <GooglePlacesAutocompleteProxy
                                        value={formik.values.address_line1}
                                        onChange={handleAddressChange}
                                        label="Company Address"
                                        required
                                        error={!!validationErrors.address_line1}
                                        helperText={validationErrors.address_line1 || "Start typing to see address suggestions"}
                                        disabled={submitting}
                                        placeholder="Enter company address..."
                                    />
                                </Box>
                                {(formik.values.city || formik.values.state || formik.values.zip_code) && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={clearAddressFields}
                                        disabled={submitting}
                                        sx={{ mt: 1, whiteSpace: 'nowrap' }}
                                    >
                                        Clear Address
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address Line 2 (Optional)"
                                name="address_line2"
                                value={formik.values.address_line2}
                                onChange={handleChange}
                                disabled={submitting}
                                placeholder="Suite, Apt, etc."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="City"
                                name="city"
                                value={formik.values.city}
                                onChange={handleChange}
                                required
                                error={!!validationErrors.city}
                                helperText={validationErrors.city}
                                disabled={submitting}
                                InputProps={{
                                    readOnly: !!formik.values.city
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="State"
                                name="state"
                                value={formik.values.state}
                                onChange={handleChange}
                                required
                                error={!!validationErrors.state}
                                helperText={validationErrors.state}
                                disabled={submitting}
                                InputProps={{
                                    readOnly: !!formik.values.state
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="ZIP Code"
                                name="zip_code"
                                value={formik.values.zip_code}
                                onChange={handleChange}
                                required
                                error={!!validationErrors.zip_code}
                                helperText={validationErrors.zip_code}
                                disabled={submitting}
                                InputProps={{
                                    readOnly: !!formik.values.zip_code
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Country"
                                name="country"
                                value={formik.values.country}
                                onChange={handleChange}
                                required
                                error={!!validationErrors.country}
                                helperText={validationErrors.country}
                                disabled={submitting}
                                InputProps={{
                                    readOnly: !!formik.values.country
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone"
                                name="phone"
                                value={formik.values.phone}
                                onChange={handleChange}
                                error={!!validationErrors.phone}
                                helperText={validationErrors.phone || "Enter in international format (e.g., +14155550199)"}
                                disabled={submitting}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formik.values.email}
                                onChange={handleChange}
                                error={!!validationErrors.email}
                                helperText={validationErrors.email}
                                disabled={submitting}
                            />
                        </Grid>
                        <Grid item xs={12}>
            <TextField
                                fullWidth
                                label="Website"
                                name="website"
                                value={formik.values.website}
                                onChange={handleChange}
                                error={!!validationErrors.website}
                                helperText={validationErrors.website || "Enter a valid URL starting with http:// or https:// (e.g., https://www.example.com)"}
                                disabled={submitting}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                                <Autocomplete
                                    fullWidth
                                    options={taxCodeOptions}
                                    getOptionLabel={(option) => `${option.id} • ${option.name}`}
                                    loading={taxCodeLoading}
                                    value={
                                        formik.values.tax_code
                                            ? taxCodeOptions.find((option) => option.id === formik.values.tax_code) || null
                                            : null
                                    }
                                    onChange={(_, newValue) => {
                                        if (newValue) {
                                            formik.setFieldValue('tax_code', newValue.id);
                                            setSelectedTaxCodeDetails({
                                                name: newValue.name,
                                                description: newValue.description,
                                            });
                                        } else {
                                            formik.setFieldValue('tax_code', '');
                                            setSelectedTaxCodeDetails(null);
                                        }
                                    }}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {option.id} — {option.name}
                                                </Typography>
                                                {option.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Product Tax Code"
                                            placeholder="Search or select a tax code"
                                            helperText={
                                                selectedTaxCodeDetails
                                                    ? selectedTaxCodeDetails.name
                                                    : 'The tax code determines how your products and services are taxed.'
                                            }
                                            disabled={submitting}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {taxCodeLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    disabled={submitting}
                                    noOptionsText={taxCodeLoading ? 'Loading tax codes…' : 'No matching tax codes'}
                                    filterOptions={(options, state) => {
                                        const input = state.inputValue.toLowerCase();
                                        return options.filter(
                                            (option) =>
                                                option.id.toLowerCase().includes(input) ||
                                                option.name?.toLowerCase().includes(input) ||
                                                option.description?.toLowerCase().includes(input)
                                        );
                                    }}
                                />
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setTaxDialogOpen(true)}
                                        disabled={submitting}
                                    >
                                        Browse
                                    </Button>
                                    {formik.values.tax_code && (
                                        <Button
                                            variant="text"
                                            color="secondary"
                                            onClick={() => {
                                                formik.setFieldValue('tax_code', '');
                                                setSelectedTaxCodeDetails(null);
                                            }}
                                            disabled={submitting}
                                            sx={{ alignSelf: 'flex-start' }}
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formik.values.is_head_office}
                                        onChange={(e) => formik.setFieldValue('is_head_office', e.target.checked)}
                                        name="is_head_office"
                                        disabled={submitting}
                                    />
                                }
                                label="Head Office Address"
                                sx={{ mb: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                                This address will be used for tax calculation and nexus determination for sales tax registration
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Box display="flex" gap={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/companies')}
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        formMode === 'create' ? 'Create' : 'Save'
                                    )}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <ProductTaxCodeDialog open={taxDialogOpen} onClose={() => setTaxDialogOpen(false)} onSelect={(code) => {
                formik.setFieldValue('tax_code', code);
                const match = taxCodeOptions.find((option) => option.id === code);
                setSelectedTaxCodeDetails(
                    match
                        ? {
                              name: match.name,
                              description: match.description,
                          }
                        : null
                );
            }} />
        </Box>
    );
}; 
